import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChevronLeft, Search, UserCheck, X, Loader2 } from "lucide-react";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

const STATUSES = ["NEW", "SCREENING", "INTERVIEW", "SALES_EXERCISE", "TRAINING",
                    "FIELD_TRIAL", "APPROVED", "ACTIVE", "PAUSED", "REJECTED", "WITHDRAWN"];

const STATUS_TONE = {
  NEW: "bg-emerald-100 text-emerald-800",
  SCREENING: "bg-sky-100 text-sky-800",
  INTERVIEW: "bg-indigo-100 text-indigo-800",
  SALES_EXERCISE: "bg-violet-100 text-violet-800",
  TRAINING: "bg-purple-100 text-purple-800",
  FIELD_TRIAL: "bg-amber-100 text-amber-800",
  APPROVED: "bg-emerald-200 text-emerald-900",
  ACTIVE: "bg-emerald-600 text-white",
  PAUSED: "bg-slate-200 text-slate-700",
  REJECTED: "bg-rose-100 text-rose-800",
  WITHDRAWN: "bg-slate-100 text-slate-500",
};

export default function RecruitmentDashboard() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const qRef = useRef("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (qRef.current) params.q = qRef.current;
      const { data } = await api.get("/recruitment/applications", { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to load applications.";
      setErr(detail);
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  if (err) return (
    <div className="max-w-2xl mx-auto p-6 text-center" data-testid="recruitment-forbidden">
      <h1 className="font-display text-2xl font-bold text-slate-900">Recruitment</h1>
      <p className="mt-3 text-sm text-rose-700">{err}</p>
      <p className="mt-2 text-xs text-slate-500">This area requires an authorised admin account.</p>
    </div>
  );

  if (selected) return <ApplicantDetail id={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="recruitment-dashboard">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Sales Agent Recruitment</h1>
      <p className="text-sm text-slate-600 mt-1">Applications from <code>/careers/sales-agent</code>.</p>

      {/* Summary cards */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {STATUSES.slice(0, 8).map(s => (
          <button key={s} onClick={() => setStatus(status === s ? "" : s)}
                   data-testid={`count-${s}`}
                   className={`rounded-xl border p-3 text-left transition ${
                     status === s ? "border-emerald-500 bg-emerald-50"
                                    : "border-slate-200 bg-white hover:bg-slate-50"}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.replace(/_/g, " ")}</div>
            <div className="mt-1 text-2xl font-bold text-slate-900">{counts[s] || 0}</div>
          </button>
        ))}
      </div>

      {/* Search + filter */}
      <div className="mt-6 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => { qRef.current = e.target.value; setQ(e.target.value); }}
                  onKeyDown={e => e.key === "Enter" && load()}
                  placeholder="Search name, email, phone, city, province…"
                  data-testid="recruitment-search"
                  className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm" />
        </div>
        <button onClick={load}
                data-testid="recruitment-refresh"
                className="px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium">Refresh</button>
        {status && <button onClick={() => setStatus("")} className="text-xs text-slate-500 underline">Clear filter: {status}</button>}
      </div>

      {/* List */}
      <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Applicant</th>
              <th className="px-3 py-2 text-left">Location</th>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-left">Sales yrs</th>
              <th className="px-3 py-2 text-left">Preference</th>
              <th className="px-3 py-2 text-left">Applied</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>
              : items.length === 0
              ? <tr><td colSpan={7} className="p-6 text-center text-slate-500">No applications yet.</td></tr>
              : items.map(a => (
                <tr key={a.id} onClick={() => setSelected(a.id)}
                    data-testid={`row-${a.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{a.full_legal_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{a.reference}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{[a.city, a.province].filter(Boolean).join(", ") || "-"}</td>
                  <td className="px-3 py-2 text-slate-700">
                    <div>{a.email}</div>
                    <div className="text-[11px] text-slate-500">{a.mobile}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{a.years_sales || "-"} / <span className="text-slate-500">{a.years_field_sales || "0"} field</span></td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{(a.work_preference || []).join(", ") || "-"}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATUS_TONE[a.status] || "bg-slate-100 text-slate-700"}`}>
                      {a.status}
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------- Applicant detail -------- */

function ApplicantDetail({ id, onBack }) {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/recruitment/applications/${id}`);
    setDoc(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/recruitment/applications/${id}`, body);
      setDoc(data);
      toast.success("Saved.");
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const sections = useMemo(() => doc ? [
    ["personal", "Personal Information", <KV rows={[
      ["Full legal name", doc.full_legal_name], ["Preferred", doc.preferred_name],
      ["DOB", doc.date_of_birth], ["Gender", doc.gender],
      ["Nationality", doc.nationality], ["Civil status", doc.civil_status],
      ["Address", doc.address], ["City", doc.city], ["Province", doc.province], ["Country", doc.country],
    ]}/>],
    ["contact", "Contact", <KV rows={[
      ["Mobile", doc.mobile], ["Email", doc.email], ["Messenger", doc.messenger],
      ["WhatsApp", doc.whatsapp], ["Viber", doc.viber], ["Telegram", doc.telegram],
      ["Preferred contact", doc.preferred_contact],
      ["Emergency contact",
        [doc.emergency_contact_name, doc.emergency_contact_number, doc.emergency_relationship]
          .filter(v => v !== undefined && v !== null && v !== "").join(" · ")],
    ]}/>],
    ["ids", "Identification / Employment (sensitive)", <KV rows={[
      ["Gov ID type", doc.gov_id_type], ["Gov ID number", doc.gov_id_number],
      ["TIN", doc.tin], ["SSS", doc.sss], ["PhilHealth", doc.philhealth], ["Pag-IBIG", doc.pagibig],
      ["Driver's license", doc.driver_license],
    ]}/>],
    ["edu", "Education & Work Experience", <KV rows={[
      ["Education", doc.education_level], ["School", doc.school], ["Course", doc.course],
      ["Year completed", doc.year_completed],
      ["Years total / sales / field",
        [doc.years_total_work, doc.years_sales, doc.years_field_sales]
          .filter(v => v !== undefined && v !== null && v !== "").join(" / ")],
      ["Titles", doc.previous_titles], ["Employers", doc.previous_employers],
      ["Description", doc.work_description],
    ]}/>],
    ["salesx", "Sales Experience", <div className="text-sm text-slate-700">{(doc.sales_experience_types || []).join(", ") || "-"}</div>],
    ["perf", "Previous Performance", <KV rows={[
      ["What sold", doc.what_sold], ["Customers", doc.customers],
      ["B2B/B2C", doc.b2b_or_b2c], ["Channel", doc.sales_channel],
      ["Prospects/week", doc.prospects_per_week], ["Typical closes", doc.typical_closes],
      ["Avg monthly", doc.avg_monthly_sales], ["Largest close", doc.largest_close],
      ["Own leads / Closed / Targets / Met",
        [doc.own_leads, doc.closed_yourself, doc.had_targets, doc.met_targets]
          .filter(v => v !== undefined && v !== null && v !== "").join(" / ")],
      ["Why left", doc.why_left],
    ]}/>],
    ["skills", "Skills (1-5)", <KV rows={Object.entries(doc.skills || {}).map(([k,v]) => [k, String(v)])}/>],
    ["lang", "Languages", <KV rows={[
      ...Object.entries(doc.languages || {}).map(([k,v]) => [k, String(v)]),
      ["Other", doc.other_languages]]}/>],
    ["field", "Field Work / Travel / Transport / Equipment", <KV rows={[
      ["Current location", doc.current_location], ["Primary area", doc.primary_area],
      ["Other areas", doc.other_areas],
      ["Field capabilities", (doc.field_capabilities || []).join(", ")],
      ["Primary transport", doc.primary_transport],
      ["Owns / registered", `${doc.owns_transport} / ${doc.vehicle_registered}`],
      ["Daily coverage", doc.daily_coverage],
      ["Equipment", (doc.equipment || []).join(", ")],
    ]}/>],
    ["avail", "Availability & Compensation", <KV rows={[
      ["Preference", (doc.work_preference || []).join(", ")],
      ["Days", (doc.available_days || []).join(", ")], ["Hours", doc.available_hours],
      ["Earliest start", doc.earliest_start],
      ["Expected monthly / daily / commission",
        `${doc.expected_monthly_salary} / ${doc.expected_daily_rate} / ${doc.expected_commission}`],
      ["OK bonuses / targets", `${doc.ok_with_bonuses} / ${doc.ok_with_targets}`],
    ]}/>],
    ["scen", "Practical Scenarios", <KV rows={Object.entries(doc.scenario_answers || {}).map(([k,v]) => [k, v])}/>],
    ["comm", "Communication Exercise", <KV rows={[
      ["30-sec pitch", doc.pitch_30s], ["One-sentence", doc.one_sentence],
      ["Discovery Q1", doc.discovery_q1], ["Discovery Q2", doc.discovery_q2], ["Discovery Q3", doc.discovery_q3],
    ]}/>],
    ["rej", "Rejection & Persistence", <KV rows={[
      ["Comfort (1-5)", String(doc.rejection_comfort)], ["After rejection", doc.after_rejection],
      ["Daily approach", doc.daily_approach_comfort], ["Rejection impact", doc.rejection_impact],
    ]}/>],
    ["ethics", "Ethics", <div className="text-sm text-slate-700">
      All {Object.values(doc.ethics || {}).filter(Boolean).length}/{Object.keys(doc.ethics || {}).length} clauses accepted.
    </div>],
    ["mot", "Motivation", <KV rows={Object.entries(doc.motivation || {}).map(([k,v]) => [k, v])}/>],
    ["refs", "References", <div className="space-y-3">
      {(doc.references || []).map((r, i) => (
        <div key={i} className="rounded border border-slate-200 p-3 text-sm">
          <div className="font-medium">{r.name || "-"} · {r.company || "-"}</div>
          <div className="text-slate-600">{r.position} · {r.relationship}</div>
          <div className="text-slate-500 text-xs">{r.phone} · {r.email}</div>
        </div>
      ))}
    </div>],
    ["files", "Resume / Files", <KV rows={[
      ["Resume", doc.resume_url], ["LinkedIn", doc.linkedin_url], ["Portfolio", doc.portfolio_url],
    ]}/>],
    ["history", "History", <div className="text-sm space-y-1">
      {(doc.history || []).map((h, i) => (
        <div key={i} className="text-slate-600">
          <span className="text-slate-400 text-xs">{new Date(h.at).toLocaleString()}</span>{": "}
          {h.action}{h.from ? ` (${h.from} → ${h.to})` : ""}{h.by ? ` · by ${h.by}` : ""}
        </div>
      ))}
    </div>],
    ["notes", "Internal Notes", <div className="text-sm space-y-2">
      {(doc.internal_notes || []).map((n, i) => (
        <div key={i} className="rounded bg-slate-50 border border-slate-200 p-2">
          <div className="text-xs text-slate-500">{new Date(n.at).toLocaleString()} · {n.by}</div>
          <div className="mt-1 whitespace-pre-wrap">{n.text}</div>
        </div>
      ))}
    </div>],
  ] : [], [doc]);

  if (!doc) return <div className="p-6 text-slate-500">Loading…</div>;
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="applicant-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-emerald-800 hover:underline">
        <ChevronLeft className="w-4 h-4" /> Back to list
      </button>
      <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">{doc.full_legal_name}</h1>
      <div className="mt-1 text-xs text-slate-500 font-mono">{doc.reference} · {doc.id}</div>

      {/* Status + notes actions */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="status-panel">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mr-2">Status</label>
          <select value={doc.status} onChange={e => patch({ status: e.target.value })}
                   data-testid="status-select"
                   className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm">
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <input placeholder="Assigned territory (optional)"
                  value={doc.assigned_territory || ""}
                  onChange={e => setDoc({ ...doc, assigned_territory: e.target.value })}
                  onBlur={e => patch({ assigned_territory: e.target.value })}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm flex-1 min-w-[180px]" />
        </div>

        {/* Approve as Promoter */}
        <ApproveAsPromoter doc={doc} onGranted={() => load()} />

        <div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                     placeholder="Add internal note (interview outcome, sales exercise result, etc.)"
                     data-testid="note-input"
                     className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <button onClick={() => note.trim() && patch({ note: note.trim() })} disabled={saving || !note.trim()}
                   data-testid="note-save"
                   className="mt-2 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-sm disabled:opacity-50">
            {saving ? "Saving…" : "Add note"}
          </button>
        </div>
      </div>

      {/* All sections in accordion */}
      <Accordion type="multiple" defaultValue={["personal", "contact"]} className="mt-6">
        {sections.map(([id, label, content]) => (
          <AccordionItem key={id} value={id}>
            <AccordionTrigger className="text-sm font-semibold">{label}</AccordionTrigger>
            <AccordionContent>{content}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function KV({ rows }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
      {(rows || []).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
        <div key={k} className="sm:contents">
          <dt className="text-xs uppercase tracking-wider text-slate-500 sm:col-span-1">{k}</dt>
          <dd className="text-slate-800 sm:col-span-2 whitespace-pre-wrap">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

/* -------- Approve as Promoter (controlled, idempotent) -------- */

function ApproveAsPromoter({ doc, onGranted }) {
  const [open, setOpen] = useState(false);
  const { panelRef, triggerRef } = useAccessibleOverlay(open, () => setOpen(false));
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const openDialog = async () => {
    setOpen(true); setErr(""); setPreview(null); setLoading(true);
    try {
      const { data } = await api.get(`/recruitment/applications/${doc.id}/promoter-preview`);
      setPreview(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to load applicant match.");
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post(
        `/recruitment/applications/${doc.id}/approve-promoter`,
        { confirm: true, note: note.trim() || undefined },
      );
      toast.success(
        data.already_granted
          ? "Promoter access was already granted: reconfirmed."
          : "Approved. Promoter access granted."
      );
      setOpen(false); setNote("");
      onGranted && onGranted();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Approve failed.");
    } finally { setSubmitting(false); }
  };

  const matched   = preview?.match?.matched;
  const account   = preview?.match?.account;
  const existing  = preview?.match?.existing_promoter;
  const roleBlocked = matched && account?.current_role && !["customer", "promoter"].includes(account.current_role);
  const inactiveBlocked = matched && account?.active === false;
  const canConfirm = matched && !roleBlocked && !inactiveBlocked;

  return (
    <div data-testid="approve-promoter-block" className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-slate-800">
          <div className="font-semibold flex items-center gap-1.5"><UserCheck className="w-4 h-4 text-emerald-800" /> Approve as Promoter</div>
          <div className="text-xs text-slate-600 mt-0.5">
            Grants the existing Promoter permission to a matched ConfirmPH account. Requires explicit confirmation.
          </div>
        </div>
        <button ref={triggerRef} onClick={openDialog}
                data-testid="approve-promoter-open"
                className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800">
          Approve as Promoter…
        </button>
      </div>

      {open && (
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="approve-promoter-title"
              data-testid="approve-promoter-dialog"
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div id="approve-promoter-title" className="font-semibold text-slate-900">Confirm promoter access</div>
              <button type="button" aria-label="Close promoter approval" onClick={() => setOpen(false)}
                      data-testid="approve-promoter-close"
                      className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              {loading && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Looking up matched account…</div>}
              {err && <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 p-2 text-xs" data-testid="approve-promoter-error">{err}</div>}

              {!loading && preview && (
                <>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-800">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Applicant</div>
                    <div className="mt-1">{doc.full_legal_name}</div>
                    <div className="text-xs text-slate-600">{doc.email}</div>
                  </div>

                  {matched ? (
                    <div className="rounded-lg border border-emerald-300 bg-white p-3" data-testid="approve-promoter-match">
                      <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold">Matched ConfirmPH account</div>
                      <div className="mt-1 text-slate-900">{account?.name || "-"} <span className="text-slate-500">·</span> <span className="text-slate-700">{account?.email}</span></div>
                      <div className="mt-1 text-xs text-slate-600">
                        Current role: <b>{account?.current_role}</b>
                        {" → "}
                        Proposed: <b className="text-emerald-800">promoter</b>
                      </div>
                      {existing?.status === "active" && (
                        <div className="mt-2 rounded bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
                          A Promoter record for this email is already active. Confirming will be treated as an idempotent reconfirmation: no duplicate role is created.
                        </div>
                      )}
                      {account?.active === false && (
                        <div className="mt-2 rounded bg-rose-50 border border-rose-200 p-2 text-xs text-rose-900" data-testid="approve-promoter-inactive-warning">
                          This account is currently deactivated/banned. Re-activate it via Admin → Users before granting promoter access.
                        </div>
                      )}
                      {account?.current_role && !["customer", "promoter"].includes(account.current_role) && (
                        <div className="mt-2 rounded bg-rose-50 border border-rose-200 p-2 text-xs text-rose-900" data-testid="approve-promoter-role-warning">
                          The matched account holds role <b>{account.current_role}</b>. To avoid locking that user out of their dashboard, this action is blocked. Ask the applicant to use a different email or resolve the role manually.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-rose-900" data-testid="approve-promoter-nomatch">
                      <div className="font-semibold">No matching ConfirmPH account.</div>
                      <div className="mt-1 text-xs">
                        We won&apos;t silently create or guess an account. Ask the applicant to create a ConfirmPH account using this exact email first, then re-run &quot;Approve as Promoter&quot;.
                      </div>
                    </div>
                  )}

                  <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                             placeholder="Optional note recorded with this approval…"
                             data-testid="approve-promoter-note"
                             className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button onClick={() => setOpen(false)}
                             className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700">
                      Cancel
                    </button>
                    <button onClick={confirm}
                             disabled={submitting || !canConfirm}
                             data-testid="approve-promoter-confirm"
                             className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                      {submitting ? "Granting…" : "Confirm & Grant Promoter Access"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
